<template>
  <!-- No aria-label here on purpose. role="alert" takes its name from the
       author, not from its contents, so an aria-label would replace the text a
       screen reader announces rather than add to it. The overlay this replaces
       set one and announced something the player could not see. -->
  <div
    :class="['hm-notice', `hm-notice--${notice.mod}`]"
    role="alert"
    data-testid="connection-notice"
  >
    <AppIcon :name="notice.icon" :size="16" class="hm-notice-glyph" />
    <span class="hm-notice-body">
      <span class="hm-notice-label">{{ notice.label }}</span>
      <span class="hm-notice-text">{{ notice.text }}</span>
    </span>
    <button
      type="button"
      class="hm-notice-x"
      :aria-label="STRINGS.aria.labels.dismissNotice"
      data-testid="dismiss-connection-notice"
      @click="emit('dismiss')"
    >
      <AppIcon name="close-x" :size="13" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import AppIcon from './ui/icons/AppIcon.vue';
import type { IconName } from './ui/icons/icon-paths';
import { STRINGS } from '@/constants/strings';

/**
 * Why the game ended. Kept as two variants rather than one because the causes
 * are genuinely different and the player can act on the difference: the network
 * dropped (danger) versus the network is fine and the server has closed the
 * game (warning).
 */
export type ConnectionNoticeVariant = 'connection-lost' | 'rejoin-failed';

const props = defineProps<{ variant: ConnectionNoticeVariant }>();

const emit = defineEmits<{ dismiss: [] }>();

const NOTICES: Record<
  ConnectionNoticeVariant,
  { mod: string; icon: IconName; label: string; text: string }
> = {
  'connection-lost': { mod: 'lost', ...STRINGS.errors.connectionNotice.connectionLost },
  'rejoin-failed': { mod: 'rejoin', ...STRINGS.errors.connectionNotice.rejoinFailed },
};

const notice = computed(() => NOTICES[props.variant]);
</script>

<style scoped>
/* Inline banner in the home screen's arena card: in flow, no fixed
   positioning, no auto-dismiss timer, no animated life bar. */
.hm-notice {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 6px 7px 11px;
  border: 1px solid;
  border-radius: 10px;
}
.hm-notice--lost {
  border-color: rgba(239, 68, 68, 0.45);
  background: rgba(239, 68, 68, 0.1);
}
.hm-notice--rejoin {
  border-color: rgba(245, 158, 11, 0.45);
  background: rgba(245, 158, 11, 0.1);
}
.hm-notice-glyph {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}
.hm-notice--lost .hm-notice-glyph {
  color: var(--color-energy-danger);
}
.hm-notice--rejoin .hm-notice-glyph {
  color: var(--color-energy-warning);
}
.hm-notice-body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
  /* Explicit because the arena card this sits in centres its text; the label
     and its line are a left-aligned stack. */
  text-align: left;
}
.hm-notice-label {
  font-family: var(--font-sans);
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-arena-text-bright);
}
.hm-notice-text {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.3;
  color: var(--color-arena-text-bright);
}
/* 44px hit target, glyph-sized visually — the negative margin keeps it from
   setting the banner's height. */
.hm-notice-x {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  margin: -7px 0;
  display: grid;
  place-items: center;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--color-arena-text-muted);
  transition: color var(--duration-normal);
}
.hm-notice-x:hover {
  color: var(--color-arena-text-bright);
}
</style>
